import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import find from "lodash/find"
import isEqual from "lodash/isEqual"
import parse from "html-react-parser"
import VizSensor from "react-visibility-sensor"
import { getSrc } from "gatsby-plugin-image"

import * as s from "./ProductDescription.module.scss"

import "react-responsive-carousel/lib/styles/carousel.min.css"
import { Carousel } from "react-responsive-carousel"

import { useSiteTranslations } from "@hooks/useSiteTranslations"
import pricePercent from "@utils/pricePercent"
import formatPrice from "@utils/formatPrice"
import trackGtagEvent from "@utils/trackGtagEvent"
import { useProductAvailability } from "@hooks/useProductAvailability"
import {
  toLocaliseShopifyProductData,
  toLocaliseShopifyVariantData,
} from "@utils/toLocaliseShopifyData"
import { addLineItemsToCart } from "@ducks/cartSlice"

import { words } from "./words"
import { AddToCartButton } from "@components/main/add-to-cart-button/AddToCartButton"
import { OutOfStockForm } from "@components/main/out-of-stock-form/OutOfStockForm"
import { ProductVariantSelector } from "@components/main/product-variant-selector/ProductVariantSelector"
import { Breadcrumbs } from "@components/main/breadcrumbs/Breadcrumbs"
import { ProductPersonalisation } from "@components/main/product-personalisation/ProductPersonalisation"
import { ProductPersonalisationLimited } from "@components/main/product-personalisation-limited/ProductPersonalisationLimited"
import { DropDown } from "@components/common/form/DropDown"
import { Tabbed } from "./Tabbed"
import { SummaryBar } from "./SummaryBar"
import { Schema } from "@components/common/schema/Schema"

export const ProductDescription = ({ product }) => {
  const {
    options,
    variants,
    variants: [initialVariant],
    productType,
  } = product

  const [personalisation, setPersonalisation] = useState("")
  const [personalisationError, setPersonalisationError] = useState(null)
  const [showSummary, setShowSummary] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)
  const dispatch = useDispatch()
  const cart = useSelector(state => state.cart)
  const [variant, setVariant] = useState({ ...initialVariant })

  if (typeof window !== "undefined") {
    window.setVariantExternally = setVariant
  }

  const {
    variantAvailable,
    variantAvailabilityMap,
  } = useProductAvailability(product, variants, variant)

  const isPersonalisedProduct =
    variantAvailable && productType === "Personalised"
  const localisedProductData = toLocaliseShopifyProductData(product)
  const localisedVariantData = toLocaliseShopifyVariantData(variant)
  const {
    shopTranslations: { shop },
  } = useSiteTranslations()

  const availableVariants = variants.filter(v => {
    const match = Object.entries(variantAvailabilityMap || {}).find(
      ([gid, isAvailable]) =>
        isAvailable && (gid === v.shopifyId || gid.endsWith(v.shopifyId))
    )
    return Boolean(match)
  })

  const isSimpleProduct =
    variants.length === 1 && variants[0].title === "Default Title"

  const handleAddToCart = () => {
    if (productType === "Personalised" && !personalisation) {
      setPersonalisationError("Please enter a name or message")
      return
    } else {
      setPersonalisationError(null)
    }

    let cartId = cart.cart.id

    dispatch(
      addLineItemsToCart({
        product: {
          ...variant,
          productTitle: localisedProductData.title,
          price: localisedVariantData.price,
          quantity: 1,
          personalisation: personalisation ? personalisation : null,
        },
        cartId,
      }),
    )
    setAddedToCart(true)

    trackGtagEvent(
      "add_to_cart",
      localisedProductData.maxVariantPrice,
      [
        {
          item_id: product.variants[0].id,
          item_name: product.title,
        },
      ],
      localisedProductData.maxVariantPriceCurrencyCode,
    )
  }

  useEffect(() => {
    const firstAvailableVariant = variants.find(
      variant => variant.availableForSale,
    )

    firstAvailableVariant && setVariant(firstAvailableVariant)
  }, [])

  const handleOptionChange = (optionIndex, value) => {
    const currentOptions = [...variant.selectedOptions]

    currentOptions[optionIndex] = {
      ...currentOptions[optionIndex],
      value,
    }

    const selectedVariant = find(variants, ({ selectedOptions }) =>
      isEqual(currentOptions, selectedOptions),
    )

    setVariant({ ...selectedVariant })
  }

  const handleVariantChange = variant => {
    setVariant({ ...variant })
  }

  const onChangeVisibility = e => {
    setShowSummary(!e)
  }

  const handlePersonalisationChange = maxLength => val => {
    if (words.some(v => val.includes(v))) {
      setPersonalisationError("Sorry, we can't accept this")
      return
    } else {
      setPersonalisationError(null)
    }

    if (val.length > maxLength) {
      setPersonalisationError("Character limit hit")
      return
    } else {
      setPersonalisationError(null)
    }

    const cleanedValue = val.toUpperCase().replace(/[^A-Z0-9 .,\n\-']/g, "")
    setPersonalisation(cleanedValue)
  }

  const PriceDisplay = () => (
    <>
      {variant?.compareAtPrice && product.handle !== "tennents-gift-card" && (
        <span className={s.ProductBlurb__price}>
          <span className={s.ProductBlurb__pricePercent}>
            {`${pricePercent(variant)} % off`}
          </span>
          <span className={s.ProductBlurb__priceCurrent}>
            Now {formatPrice(localisedVariantData.price, localisedVariantData.priceCurrencyCode)}
          </span>
          <span className={s.ProductBlurb__pricePrevious}>
            Was {formatPrice(localisedVariantData.compareAtPrice, localisedVariantData.compareAtPriceCurrencyCode)}
          </span>
        </span>
      )}

      {variants.length < 2 && (
        <h4 className={s.ProductBlurb__price}>
          {formatPrice(localisedVariantData.price, localisedVariantData.priceCurrencyCode)}
        </h4>
      )}
    </>
  )

  return (
    <>
      <SummaryBar
        product={product}
        variant={variant}
        vis={showSummary}
        handleAddToCart={handleAddToCart}
        addedToCart={addedToCart}
        variantAvailable={variantAvailable}
      />

      <div className={s.ProductDescriptionContainer}>
        <VizSensor partialVisibility onChange={e => onChangeVisibility(e)}>
          <div className={s.ProductDescription}>
            <div className={`${s.ProductGallery} ProductGallery`}>
              <div className={s.ProductGallery__carouselSwiper}>
                <Carousel
                  showThumbs={false}
                  showStatus={false}
                  showIndicators={product.media.length > 1}
                  swipeable={false}
                >
                  {product.media.map(({ image }, i) => (
                    <div key={i}>
                      <img
                        src={getSrc(image.gatsbyImageData)}
                        alt={image.altText || localisedProductData.title}
                      />
                    </div>
                  ))}
                </Carousel>
              </div>
            </div>

            <div className={s.ProductBlurb}>
              <Breadcrumbs productType={product.productType} />
              <h1 className={s.ProductBlurb__title}>{localisedProductData.title}</h1>
              <PriceDisplay />

              <div className={s.ProductBlurb__description}>
                {parse(localisedProductData.description)}
              </div>

              {product.handle === "tennents-gift-card" ? (
                <DropDown
                  placeholder={"Select gift card value"}
                  options={options[0].values.map(option => ({
                    label: option,
                    value: option,
                  }))}
                  value={variant.title}
                  onChange={val => handleOptionChange(0, val)}
                />
              ) : (
                !isSimpleProduct && availableVariants.length > 0 && (
                  <ProductVariantSelector
                    variants={availableVariants}
                    activeVariant={variant}
                    setVariant={handleVariantChange}
                  />
                )
              )}

              {isPersonalisedProduct &&
                (product.handle === "personalised-tennents-tankard" ? (
                  <ProductPersonalisationLimited
                    className={s.ProductPersonalisation}
                    value={personalisation}
                    onChange={handlePersonalisationChange(25)}
                    error={personalisationError}
                  />
                ) : (
                  <ProductPersonalisation
                    className={s.ProductPersonalisation}
                    value={personalisation}
                    onChange={handlePersonalisationChange(36)}
                    error={personalisationError}
                  />
                ))}

              {variantAvailable !== undefined &&
                !personalisationError &&
                (variantAvailable ? (
                  <AddToCartButton
                    className={s.ProductBlurb__addToCart}
                    onClick={handleAddToCart}
                    title={shop.addToCartCtaText}
                    addedTitle={shop.addedToCartCtaText}
                    added={addedToCart}
                  />
                ) : (
                  <OutOfStockForm variant={variant} />
                ))}
            </div>
          </div>
        </VizSensor>
      </div>

      <div className={s.ProductContentContainer}>
        <div className={s.ProductContentContainer__copy}>
          {parse(localisedProductData.description)}
        </div>
        <Tabbed productDescription={localisedProductData.description} />
      </div>

      <Schema data={{
        "@context": "http://schema.org",
        "@type": "Product",
        "name": `${product.title}`,
        "url": `https://www.tennents.com/${process.env.GATSBY_LOCALE}/shop/${product.handle}`,
        "image": `${product.media[0]?.image?.originalSrc}`,
        "description": `${product.metafields?.find(m => m.key === "description_tag")?.value}`,
        "brand": {
          "@type": "Brand",
          "name": "Tennent's"
        },
        "offers": [product.variants.map((variant) => ({
          "@type": "Offer",
          "priceCurrency": product.contextualPricing.maxVariantPricing.price.currencyCode,
          "price": variant.price,
          "itemCondition": "https://schema.org/NewCondition",
          "availability": variantAvailabilityMap[variant.shopifyId]
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          "url": `https://www.tennents.com/${process.env.GATSBY_LOCALE}/shop/${product.handle}?variant=${variant.id}`,
        }))
        ]
      }} />
    </>
  )
}